<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { trackEvent } from '@/composables/useAnalytics'
import { Head, Link } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import VideoPlayer from '@/Components/Library/VideoPlayer.vue'
import VideoMetadata from '@/Components/Library/VideoMetadata.vue'
import TutorInfo from '@/Components/Library/TutorInfo.vue'
import Comments from '@/Components/Library/Comments.vue'
import RelatedVideos from '@/Components/Library/RelatedVideos.vue'
import { ArrowLeft } from 'lucide-vue-next'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface TeacherContent {
  id: number
  slug: string
  title: string
  description: string
  duration?: number
  view_count: number
  created_at: string
  is_free?: boolean
  subject?: {
    name: string
    color?: string
  }
  level?: {
    name: string
  }
  examboard?: {
    name: string
  }
  teacher: {
    id: number
    slug?: string
    name: string
    avatar?: string
    bio?: string
    specialization?: string
    video_count?: number
    student_count?: number
  }
  comments_count?: number
  ratings_count: number
  ratings_avg_score?: number
}

interface AttachmentUrl {
  url: string
  original_name: string
  size: number
}

interface Props {
  content: TeacherContent
  streamUrl?: string | null
  documentUrl?: string | null
  attachmentUrls?: AttachmentUrl[]
  hasSubscription: boolean
  canComment: boolean
  relatedVideos?: TeacherContent[]
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  streamUrl: null,
  documentUrl: null,
  attachmentUrls: () => [],
  relatedVideos: () => [],
  subscriptions: () => [],
})

const isNew = computed(() => {
  const publishedDate = new Date(props.content.created_at)
  const now = new Date()
  const daysDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24)
  return daysDiff <= 7
})

const isSubscribedToTeacher = computed(() =>
  props.subscriptions.some((sub) => sub.teacher_id === props.content.teacher.id)
)

const videoStats = computed(() => ({
  likes: 142,
  dislikes: 3,
  shares: 28,
}))

onMounted(() => {
  trackEvent('viewContent', {
    content_type: 'video',
    content_id: props.content.id,
    content_name: props.content.title,
    tutor_name: props.content.teacher.name,
    tutor_id: props.content.teacher.id,
    subject: props.content.subject?.name,
    level: props.content.level?.name,
  })
})
</script>

<template>
  <Head :title="content.title" />

  <DashboardLayout>
    <div class="w-full max-w-7xl">
      <!-- Back Button -->
      <Link
        href="/student/explore?tab=library"
        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeft class="w-4 h-4" />
        Back to Library
      </Link>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Video Player -->
          <VideoPlayer
            :stream-url="streamUrl"
            :document-url="documentUrl"
            :has-subscription="hasSubscription"
            :teacher-slug="content.teacher.slug"
            tutor-link-prefix="/student/tutors"
          />

          <!-- Attachment Downloads -->
          <div v-if="attachmentUrls.length > 0" class="bg-white rounded-xl border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-900 mb-3">Supporting Materials</h3>
            <div class="flex flex-col gap-2">
              <a
                v-for="(attachment, index) in attachmentUrls"
                :key="index"
                :href="attachment.url"
                target="_blank"
                class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span class="material-symbols-outlined text-red-500">picture_as_pdf</span>
                <span class="text-sm text-gray-700">{{ attachment.original_name }}</span>
                <span class="material-symbols-outlined text-gray-400 ml-auto text-base">download</span>
              </a>
            </div>
          </div>

          <!-- Video Metadata -->
          <VideoMetadata
            :title="content.title"
            :description="content.description"
            :subject="content.subject"
            :level="content.level"
            :is-new="isNew"
            :is-free="content.is_free"
            :published-at="content.created_at"
            :view-count="content.view_count"
            :likes="videoStats.likes"
            :dislikes="videoStats.dislikes"
            :shares="videoStats.shares"
          />

          <!-- Tutor Info -->
          <TutorInfo
            :tutor="{
              id: content.teacher.id,
              slug: content.teacher.slug,
              name: content.teacher.name,
              avatar: content.teacher.avatar,
              specialization: content.teacher.specialization || 'Expert Tutor',
              videoCount: content.teacher.video_count || 0,
              studentCount: content.teacher.student_count || 0,
            }"
            tutor-link-prefix="/student/tutors"
          />

          <!-- Comments -->
          <Comments :content-id="content.id" :initial-count="content.comments_count || 0" :readonly="!canComment" />
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Related Videos -->
          <RelatedVideos
            :videos="relatedVideos.map((v) => ({
              id: v.id,
              slug: v.slug,
              title: v.title,
              thumbnail: v.thumbnail,
              duration: v.duration,
              tutor: v.teacher,
              view_count: v.view_count,
              created_at: v.created_at,
            }))"
            base-path="/student/library"
          />

          <!-- Subscribe CTA (if not subscribed to this teacher) -->
          <div
            v-if="!isSubscribedToTeacher && !hasSubscription"
            class="bg-gradient-to-br from-teal-500 to-teal-800 rounded-2xl shadow-lg p-6 text-white"
          >
            <h3 class="font-bold text-xl mb-2">Unlock Full Access</h3>
            <p class="text-sm opacity-90 mb-4">
              Subscribe to watch unlimited educational content from expert tutors
            </p>
            <ul class="space-y-2 mb-4 text-sm">
              <li class="flex items-center">
                <div class="w-1.5 h-1.5 bg-white rounded-full mr-2" />
                Unlimited video access
              </li>
              <li class="flex items-center">
                <div class="w-1.5 h-1.5 bg-white rounded-full mr-2" />
                All subjects & levels
              </li>
              <li class="flex items-center">
                <div class="w-1.5 h-1.5 bg-white rounded-full mr-2" />
                Expert tutor support
              </li>
            </ul>
            <Link
              v-if="content.teacher.slug"
              :href="`/student/tutors/${content.teacher.slug}`"
              class="block w-full px-4 py-3 bg-white text-blue-600 rounded-xl font-bold hover:bg-gray-100 transition-colors text-center"
            >
              View Plans
            </Link>
          </div>

          <!-- Already Subscribed -->
          <div
            v-if="isSubscribedToTeacher"
            class="bg-green-50 border border-green-200 rounded-xl p-4"
          >
            <p class="text-green-800 font-semibold text-sm">Subscribed to {{ content.teacher.name }}</p>
            <p class="text-green-600 text-xs mt-1">You have full access to this content</p>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
